import React, { Suspense, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import strings from 'utils/strings'
import ContextualHelp from 'components/ContextualHelp'
import {
  Flex,
  Paragraph,
  RadioButtonGroup,
  SelectMenu,
} from 'components/patterns'
import { themeColor } from 'utils/styles'
import {
  Icon__PinLocation as IconPinLocation,
  Icon__Alert as IconAlert,
} from 'components/patterns/Icon/components'
import { Tooltip } from 'antd'
import { ContextualHelpBox } from 'components/ContextualHelp/ContextualHelp.style'
import { Icon } from 'components/patterns/Icon/Icon'
import { yup } from 'schemas/validation'
import { useSelector } from 'hooks/redux'
import { VEHICLE_SUBTYPES } from 'utils/constants'
import featuresStrings from 'utils/strings/features-strings'
import { getConnectorRestrictions } from 'utils/pinRestrictions'

function getModuleName(state) {
  const featureId = getCurrentFeatureId(state)
  return state.getIn([
    'feature',
    'currentFeature',
    'feature',
    featureId,
    'moduleName',
  ])
}

function getCurrentFeatureId(state) {
  return state.getIn(['feature', 'currentFeature', 'feature']).keySeq().first()
}

function getFeatureVehicleType(state) {
  const featureId = getCurrentFeatureId(state)
  return state.getIn([
    'feature',
    'currentFeature',
    'feature',
    featureId,
    'vehicleSubType',
  ])
}

export function OutputConnectorFinder({ output, ...props }) {
  const location = useSelector((state) => {
    const { mappedOutputId } = output
    const moduleName = getModuleName(state)
    const { description, connector, location } = state
      .getIn(['feature', 'outputPins', moduleName, mappedOutputId])
      .toJS()
    return `${description}: ${connector}-${location}`
  })
  return <ConnectorFinder {...props} location={location} type="output" />
}

OutputConnectorFinder.propTypes = {
  output: PropTypes.shape({
    mappedOutputId: PropTypes.string.isRequired,
  }).isRequired,
}

export function InputConnectorFinder({ inputUri, ...props }) {
  const location = useSelector((state) => {
    const moduleName = getModuleName(state)
    const signal = state.getIn(['feature', 'signals', moduleName, inputUri])
    if (!signal) return null
    const { description, connector, location } = signal.toJS()
    return `${description}: ${connector}-${location}`
  })
  if (!location) return null
  return <ConnectorFinder {...props} location={location} type="input" />
}

InputConnectorFinder.propTypes = {
  inputUri: PropTypes.string.isRequired,
}

export default function ConnectorFinder({ interfacePins, ...props }) {
  const vehicleSubType = useSelector(getFeatureVehicleType)
  const [isOpen, setIsOpen] = useState(false)

  if (
    !interfacePins ||
    interfacePins.length === 0 ||
    vehicleSubType === VEHICLE_SUBTYPES.BUS
  )
    return null

  return (
    <SimpleCollapsibleContainer>
      <SimpleCollapsibleHeader onClick={() => setIsOpen(!isOpen)}>
        <span>{strings.find_factory_interface_connector_helper}</span>
        <CaretIcon isOpen={isOpen} />
      </SimpleCollapsibleHeader>
      {isOpen && (
        <ContextualHelp
          contentMsg={<ConnectorTable {...{ interfacePins, ...props }} />}
          icon={
            <Suspense fallback="">
              <IconPinLocation width={20} height={20} />
            </Suspense>
          }
        />
      )}
    </SimpleCollapsibleContainer>
  )
}

ConnectorFinder.defaultProps = {
  interfacePins: [],
}

ConnectorFinder.propTypes = {
  interfacePins: PropTypes.array,
}

export function AnalogInputHelper({ selectedPin, referencePin }) {
  return (
    <Flex flexDirection="column">
      <StyledAnalogInputParagraph>{`${strings.analog_input_interface_connector_helper}:`}</StyledAnalogInputParagraph>
      <ContextualHelp
        contentMsg={
          <Flex flexDirection="column" gap="16px">
            <span>
              {strings.analog_input_interface_connector_helper_title}:
            </span>
            <Flex flexDirection="column">
              <Paragraph bold>{selectedPin}</Paragraph>
              <Paragraph bold>{referencePin}</Paragraph>
            </Flex>
            <Paragraph>
              {strings.analog_input_interface_connector_helper_text}
            </Paragraph>
          </Flex>
        }
        icon={
          <Suspense fallback="">
            <IconPinLocation width={20} height={20} />
          </Suspense>
        }
      />
    </Flex>
  )
}

AnalogInputHelper.propTypes = {
  selectedPin: PropTypes.string,
  referencePin: PropTypes.string,
}

export function ConnectorTable({ interfacePins = [], location, type }) {
  const possibleVehicleTypes = (interfacePins || []).map(
    ({ vehicleSubType }) => vehicleSubType
  )
  const [vehicleType, updateVehicleType] = useState(possibleVehicleTypes[0])

  const matchedInterface = (interfacePins || []).find(
    ({ vehicleSubType }) => vehicleType === vehicleSubType
  )
  const { pins = {} } = matchedInterface || {}

  const possibleSalesCodes = Object.entries(pins).map(
    ([key, { isMapped }]) => ({ key, disabled: isMapped === false })
  )
  const [salesCode, updateSalesCode] = useState(
    possibleSalesCodes.length > 0 ? possibleSalesCodes[0].key : null
  )

  if (!interfacePins || interfacePins.length === 0) return null

  const handleUpdateVehicleType = ({ target: { value } }) => {
    updateVehicleType(value)
    const newInterface = interfacePins.find(
      ({ vehicleSubType }) => value === vehicleSubType
    )
    if (newInterface) {
      const newSalesCodes = Object.keys(newInterface.pins || {})
      // Only reset salesCode if current selection is not available in new vehicle type
      if (!newSalesCodes.includes(salesCode) && newSalesCodes.length > 0) {
        updateSalesCode(newSalesCodes[0])
      }
    }
  }

  const handleUpdateSalesCode = (value) => {
    updateSalesCode(value)
  }

  const vehicleTypes = possibleVehicleTypes.map((type) => ({
    value: type,
    text: type,
    disabled: false,
  }))

  const salesCodes = possibleSalesCodes.map(({ key }) => ({
    key,
    value: key,
    title: key,
  }))

  const {
    extensionConnector = null,
    extensionPin = null,
    isVpdmOutput: isPinVpdmOutput = false,
    module = null,
    message = null,
  } = vehicleType && salesCode
    ? interfacePins.reduce((acc, item) => {
        if (item.vehicleSubType === vehicleType) {
          const pinData = item.pins?.[salesCode]
          if (pinData) {
            return {
              ...pinData,
              module: item.module,
            }
          }
        }
        return acc
      }, {})
    : {}

  const connector =
    extensionConnector && extensionPin
      ? [extensionConnector, extensionPin].join(', ')
      : null

  const isInput = type === 'input'

  // Get connector restrictions for date-based variations
  const {
    hasSameLocation,
    connectorBefore,
    isPinVpdmOutputBefore,
    messageBefore,
  } = getConnectorRestrictions({
    interfacePins,
    vehicleType,
    module,
    salesCode,
    connector,
  })

  const hasDbCodeRestrictions = connectorBefore !== null

  return (
    <>
      <span>{strings.find_factory_interface_connector(location)}:</span>
      <StyledFields>
        <StyledFieldDiv>
          <StyledSelectLabel>{strings.vehicle_type}:</StyledSelectLabel>
          <StyledRadioButtonGroup
            currentValue={vehicleType}
            onChange={handleUpdateVehicleType}
            buttons={vehicleTypes}
          />
        </StyledFieldDiv>
        <StyledFieldDivWithMinWidth>
          <StyledSelectLabel>
            {strings.quickfit_sales_code}:{' '}
            <Tooltip title={strings.quickfit_sales_code_help} placement="left">
              <Icon fill="outline" icon="question_mark_tooltip" size={14} />
            </Tooltip>
          </StyledSelectLabel>
          <SelectMenu
            options={salesCodes}
            value={salesCode}
            onChange={handleUpdateSalesCode}
            showSearch
            name={strings.select}
            rules={yup.string().nullable()}
            width={90}
          />
        </StyledFieldDivWithMinWidth>
      </StyledFields>
      {possibleSalesCodes.length === 0 && (
        <NoPinDataMessage>
          {strings.not_available}
          {' \u2014 '}
          Pin location data is not available for this vehicle type.
        </NoPinDataMessage>
      )}
      {connector && (
        <StyledLocationLabel>
          {strings.pin_location(isInput)}:
          <div>
            {(hasSameLocation || !hasDbCodeRestrictions
              ? [
                  {
                    name: connector,
                    label: 'default',
                    showVpdmWarning: isPinVpdmOutput || message,
                    message,
                  },
                ]
              : [
                  {
                    name: connector,
                    label: featuresStrings.built_before_date,
                    showVpdmWarning: isPinVpdmOutput || message,
                    message,
                  },
                  {
                    name: connectorBefore,
                    label: featuresStrings.built_after_date,
                    showVpdmWarning: isPinVpdmOutputBefore || messageBefore,
                    message: messageBefore,
                  },
                ]
            ).map((item) => (
              <LocationRow key={`${item.name}-${item.label}`}>
                {item.showVpdmWarning && (
                  <Tooltip
                    title={(item.message || strings.vpdm_warning).replace(
                      /([^.])$/,
                      '$1.'
                    )}
                  >
                    <StyledTooltipSpan>
                      <IconWrapper />
                    </StyledTooltipSpan>
                  </Tooltip>
                )}

                <div>
                  <strong>{strings.connector_location(item.name)}</strong>
                  {item.label !== 'default' && (
                    <RestrictionNote>{` (${item.label})`}</RestrictionNote>
                  )}
                </div>
              </LocationRow>
            ))}
          </div>
        </StyledLocationLabel>
      )}
    </>
  )
}

ConnectorTable.propTypes = {
  interfacePins: PropTypes.array,
  location: PropTypes.string,
  type: PropTypes.string,
}

const SimpleCollapsibleContainer = styled.div`
  ${ContextualHelpBox} {
    margin: 0;
  }
`

const SimpleCollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0;
  padding-bottom: 20px;
  margin-top: 12px;
  color: ${themeColor('primaryPurple')};
  font-size: 12px;
`

const CaretIcon = styled.span`
  display: inline-block;
  margin-left: 5px;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  ${({ isOpen }) =>
    isOpen
      ? 'border-bottom: 6px solid currentColor;'
      : 'border-top: 6px solid currentColor;'}
`

// styled component providing flex box rules for the fields in two columns 50% width each
const StyledFields = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  width: 100%;
  flex-flow: row wrap;
  overflow: visible !important;
`

const StyledSelectLabel = styled.div`
  font-size: 12px;
`

const StyledLocationLabel = styled.div`
  margin-top: 16px;
`

const LocationRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
`

const RestrictionNote = styled.span`
  color: var(--Black-Black-Secondary, rgba(7, 7, 15, 0.7));
  font-family: Arial, sans-serif;
  font-size: 10px;
  font-style: italic;
  font-weight: 400;
  line-height: 18px;
`

const IconWrapper = styled(IconAlert)`
  margin-right: 2px;
  margin-bottom: 5px;
  vertical-align: middle;
  flex-shrink: 0;
  display: block;
`

const NoPinDataMessage = styled.div`
  margin-top: 16px;
  font-size: 12px;
  font-style: italic;
  color: var(--Black-Black-Secondary, rgba(7, 7, 15, 0.7));
`

const StyledAnalogInputParagraph = styled(Paragraph)`
  color: rgba(108, 24, 186, 1);
  font-size: 12px;
  margin-top: 12px;
`

const StyledFieldDiv = styled.div`
  flex: 0 1 auto;
`

const StyledFieldDivWithMinWidth = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  position: relative;
  z-index: 10;

  .ant-select-dropdown {
    top: 32px !important;
    z-index: 9999 !important;
  }
`

const StyledTooltipSpan = styled.span`
  display: inline-flex;
`

const StyledRadioButtonGroup = styled(RadioButtonGroup)`
  .ant-radio-button-wrapper {
    padding: 0 10px !important;
    font-size: 14px !important;
    height: 32px !important;
    line-height: 30px !important;
  }
`
